<template>
	<div
		class="field-item"
		:class="[isFieldItemGrow, isLabelInline, fieldClassname, disabledClassname]"
	>
		<label
			v-if="label"
			class="field-label"
			:class="[labelClassname, labelInlineClassname]"
		>{{ label }}</label>
		<div
			v-click-outside="selectClose"
			:class="selectClassname"
			class="select"
		>
			<div @click="selectToggle" class="select__header">
				<input
					v-model="filterString"
					v-if="useFilter"
					v-show="isFiltering"
					:placeholder="!tagMode && !multiselect ? selectedValue : ''"
					@input="optionsFilter($event)"
					type="text"
					class="select__value select__input"
					:class="{'select__input--tag': tagMode}"
					ref="selectInput"
				>
				<div
					:class="[placeholderClassname, placeholderClassnameMultiselect, tagModeClassname, filteringClassname, nonFunctionalClassname]"
					class="select__value"
					:aria-label="ariaLabel"
					v-html="selectedValue"
				>
				</div>
				<div
					:class="tagModeIconClassname"
          v-if="!nonFunctional"
					class="select__icon"
				>
					<i v-if="!tagMode" class="fas fa-chevron-down"></i>
					<i v-if="tagMode" class="fas fa-plus-square"></i>
				</div>
			</div>
			<div
				:class="[dropdownActiveClassname, directionClassname]"
				class="select__dropdown"
			>
				<div
					v-for="(option, $optionIndex) in optionsFiltered"
					:key="$optionIndex"
					:aria-label="property ? option[property] : option"
					:class="{
						current: !multiselected &&
						 (!property || !selected ?
						 option === selected :
						  !byValue ?
						   option[property] === selected[property] :
						   option[byValue] === selected),
						disabled: optionsAvailable && !optionsAvailable.some(o => optionValue(o, property) === optionValue(option, property)),
						multiselected: multiselect && multiselected?.length && multiselected.some(o => optionValue(o, property) === optionValue(option, property))
					}"
					@click="selectChoose($optionIndex, option)"
					class="select__item"
				>{{ property ? option[property] : option }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {clickOutside} from '@/directives/click-outside';
import {ref, computed, nextTick, watch} from 'vue';

export default {
	directives: {
		clickOutside,
	},
	props: {
		options: {
			type: Array,
		},
		optionsAvailable: {
			type: [Array, Object],
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
    nonFunctional: {
      type: Boolean,
      default: false
    },
		selected: {
			type: [String, Number, Object]
		},
		multiselect: {
			type: Boolean,
			default: false
		},
		multiselected: {
			type: Array
		},
		useFilter: {
			type: Boolean,
			default: false
		},
		tagMode: {
			type: Boolean,
			default: false
		},
		property: {
			// which property to use to show in dropdown
			type: [String, Object],
			default: null,
		},
		byValue: {
			// can only be used with property,
			// which property is used to provide selected
			type: String,
			default: ''
		},
		direction: {
			type: String,
			default: 'bottom'
		},
		selectClassname: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String
		},
		label: {
			type: String,
			default: ''
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		labelClassname: {
			type: String,
			default: ''
		},
		labelInline: {
			type: Boolean,
			default: false
		},
		fieldItemGrow: {
			type: Boolean,
			default: true
		},
		fieldClassname: {
			type: String,
			default: ''
		},
		useChangeEmit: {
			type: Boolean,
			default: false
		}
	},
	emits: {
		'update:selected': () => true,
		'update:multiselected': () => true,
		'change-selected': () => true,
		'change-multiselected': () => true,
		'change': () => true
	},
	setup(props, {emit}) {
		// OPENED STATUS
		const opened = ref(false);
		const dropdownActiveClassname = computed(() => opened.value ? "opened" : "");
		
		// SELECTED VALUE
		const selectSettings = computed(() => ({
			selected: props.selected,
			placeholder: props.placeholder,
			multiselected: props.multiselected,
			tagMode: props.tagMode,
			multiselect: props.multiselect,
			property: props.property,
			byValue: props.byValue,
			options: props.options
		}))
		const selectedValue = computed(() => getComputedSelectedValue(selectSettings.value));
		
		function getComputedSelectedValue(settings) {
			const {selected, placeholder,	multiselected, multiselect, tagMode, property, byValue,	options} = settings;
			let result = null;
			// no selected or multi selected and placeholder provided -> show placeholder instead of chosen value
			if ((!selected || (multiselect && !multiselected.length)) && placeholder) result = placeholder;
			// when more than one item selected, show caption
			if (multiselect && multiselected?.length) {
				if (tagMode && !property) result = multiselected.map(value => `<span>${value}</span>`).join("");
				if (tagMode && property) result = multiselected.map(value => `<span>${value[property]}</span>`).join("");
				if (!tagMode) result = `(${multiselected.length}) values chosen`;
			}
			// property means options are array of objects and the property specified which value of the object should be shown
			if (property && !byValue && selected) result = selected[property];
			// byValue specified which of the properties is used to provide selected and return to parent, that means selected is string
			// byValue can only be used alongside the property
			if (byValue && selected !== null && selected !== undefined && selected !== 'None') {
				const match = options.find(option => option[byValue] === selected);
				if (match) result = match[property];
			}
			if (!result) result = selected;
			return result;
		}
		
		// FILTERING
		const optionsFiltered = ref(props.options);
		const filterString = ref('');

		// Options are not always known when the field is rendered: a list of
		// providers, clients or fonts is fetched. Without this the field
		// keeps the snapshot it was created with and never shows what
		// arrived, while an open filter keeps the term it was typed with.
		watch(() => props.options, options => {
			optionsFiltered.value = filterString.value
				? filterOptions(options, filterString.value)
				: options;
		});
		const isFiltering = ref(false);
		const filteringClassname = computed(() => isFiltering.value ? 'filtering' : '');
		const selectInput = ref(null);
		
		function filterOptions(options, value) {
			const term = (value || '').toLowerCase();
			return options.filter(option =>
				props.property ?
					option[props.property].toLowerCase().includes(term) :
					option.toLowerCase().includes(term)
			);
		}

		function optionsFilter(event) {
			optionsFiltered.value = filterOptions(props.options, event.target.value);
		}
		
		// CLASSNAMES
		const placeholderClassname = computed(() => !props.multiselect && !props.selected && props.placeholder ? "placeholder" : "");
		const placeholderClassnameMultiselect = computed(() => props.multiselect && !props.multiselected?.length && props.placeholder ? "placeholder" : "");
		const disabledClassname = computed(() => props.disabled ? 'disabled' : '');
    const nonFunctionalClassname = computed(() => props.nonFunctional ? 'non-functional' : '');
		const directionClassname = computed(() => props.direction)
		
		const isFieldItemGrow = computed(() => props.fieldItemGrow ? "field-item--grow" : "");
		const isLabelInline = computed(() => props.labelInline ? "" : "field-item--column");
		const labelInlineClassname = computed(() => props.labelInline ? "" : "mb--15");
		
		// TAG MODE
		const tagModeClassname = computed(() => props.tagMode ? "tag-mode" : "");
		const tagModeIconClassname = computed(() => props.tagMode ? "tag-mode__icon" : "");
		
		// UTILS
		function optionValue(item, property) {
			return property ? item[property] : item;
		}
		
		function checkIfOptionSelected(checkedOptions, option, property) {
			return checkedOptions.some(item => checkOptionsEquity(item, option, property))
		}
		
		function checkOptionsEquity(item, option, property) {
			return optionValue(item, property) === optionValue(option, property)
		}
		
		const selectedSettings = computed(() => ({
			optionsAvailable: props.optionsAvailable,
			property: props.property,
			multiselect: props.multiselect,
			multiselected: props.multiselected,
			byValue: props.byValue,
			useFilter: props.useFilter
		}));
		
		// SELECT ACTIONS
		function selectChoose(optionIndex, option) {
			const {optionsAvailable, property, multiselect, multiselected, byValue, useFilter} = selectedSettings.value;
			if (optionsAvailable && !checkIfOptionSelected(optionsAvailable, option, property)) return;
			if (multiselect) {
				checkIfOptionSelected(multiselected, option, property) ?
					updateMultiselected(multiselected.filter(item => !checkOptionsEquity(item, option, property))) :
					updateMultiselected([...multiselected, option]);
					if(useFilter) selectInput.value.focus();
			} else {
				byValue ? updateSelected(option[byValue]) : updateSelected(option);
				selectClose();
			}
		}
		
		function selectToggle() {
			if (props.disabled || props.nonFunctional) return;
			if (props.useFilter) {
				isFiltering.value = !isFiltering.value;
				nextTick(() => {
					selectInput.value.focus();
				})
			}
			opened.value = !opened.value;
			optionsFiltered.value = props.options;
		}
		
		function selectClose() {
			isFiltering.value = false;
			filterString.value = '';
			opened.value = false;
		}
		
		// EMITTERS
		function updateMultiselected(selected) {
      emit('change', selected);
			if (props.useChangeEmit) emit('change-multiselected', selected);
			if (!props.useChangeEmit) emit('update:multiselected', selected);
		}
		
		function updateSelected(selected) {
      emit('change', selected);
			if (props.useChangeEmit) emit('change-selected', selected);
			if (!props.useChangeEmit) emit('update:selected', selected);
		}
		
		return {
			dropdownActiveClassname,
			placeholderClassname,
			placeholderClassnameMultiselect,
			disabledClassname,
      nonFunctionalClassname,
			selectedValue,
			isFieldItemGrow,
			isLabelInline,
			labelInlineClassname,
			directionClassname,
			optionValue,
			selectChoose,
			selectToggle,
			selectClose,
			tagModeClassname,
			tagModeIconClassname,
			optionsFiltered,
			filterString,
			isFiltering,
			filteringClassname,
			optionsFilter,
			selectInput,
		};
	}
}
</script>

<style lang="scss">
@use "@/scss/base/fields/select";
</style>
